import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Button } from 'primeng/button';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { ChecklistTemplateService, ChecklistTemplate } from '../../services/checklist-template.service';
import { ProjectChecklistService, ProjectChecklist, UpsertProjectChecklistItemDto } from '../../services/project-checklist.service';

interface ItemRow {
  itemId: string;
  question: string;
  displayOrder: number;
  isActive: boolean;
  answer: string;
  comment: string;
}

interface TemplateState {
  template: ChecklistTemplate;
  rows: ItemRow[];
  expanded: boolean;
  isDirty: boolean;
  saving: boolean;
  loading: boolean;
  loaded: boolean;
}

@Component({
  selector: 'app-project-checklist',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Button,
    Select,
    Tooltip
  ],
  templateUrl: './project-checklist.component.html',
  styleUrl: './project-checklist.component.scss',
  providers: [MessageService]
})
export class ProjectChecklistComponent implements OnInit, OnChanges {
  @Input() projectId!: string;
  @Output() dirtyChange = new EventEmitter<boolean>();

  private destroyRef = inject(DestroyRef);

  templateStates: TemplateState[] = [];
  loading = false;

  readonly answerOptions = [
    { label: '— Select —', value: '' },
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
    { label: 'N/A', value: 'NA' }
  ];

  constructor(
    private templateService: ChecklistTemplateService,
    private checklistService: ProjectChecklistService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    if (this.projectId) this.loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] && !changes['projectId'].firstChange && this.projectId) {
      this.emitDirty(false);
      this.loadData();
    }
  }

  loadData(): void {
    this.loading = true;
    this.templateService.getActive().pipe(
      switchMap(templates => {
        if (!templates.length) return of({ templates, answers: [] as ProjectChecklist[][] });
        return forkJoin(
          templates.map(t =>
            this.checklistService.getByProjectAndTemplate(this.projectId, t.id).pipe(catchError(() => of([] as ProjectChecklist[])))
          )
        ).pipe(
          switchMap(answerArrays => of({ templates, answers: answerArrays }))
        );
      }),
      catchError(() => of({ templates: [] as ChecklistTemplate[], answers: [] as ProjectChecklist[][] })),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: ({ templates, answers }) => {
        this.templateStates = templates.map((t, idx) => {
          const templateAnswers: ProjectChecklist[] = Array.isArray(answers) ? (answers[idx] ?? []) : [];
          const answerMap = new Map(templateAnswers.map(a => [a.checklistItemId, a]));
          const rows: ItemRow[] = (t.items ?? []).map(item => {
            const existing = answerMap.get(item.id);
            return {
              itemId: item.id,
              question: item.question,
              displayOrder: item.displayOrder,
              isActive: item.isActive,
              answer: existing?.answer ?? '',
              comment: existing?.comment ?? ''
            };
          });
          return {
            template: t,
            rows,
            expanded: false,
            isDirty: false,
            saving: false,
            loading: false,
            loaded: true
          };
        });
        this.emitDirty(false);
        this.loading = false;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load checklist.' });
        this.loading = false;
      }
    });
  }

  toggleExpand(state: TemplateState): void {
    state.expanded = !state.expanded;
  }

  onAnswerChange(state: TemplateState): void {
    if (!state.isDirty) {
      state.isDirty = true;
      this.emitDirty(true);
    }
  }

  saveTemplate(state: TemplateState): void {
    const missingComments = state.rows.filter(r => r.isActive && r.answer === 'No' && !r.comment?.trim());
    if (missingComments.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Comment Required',
        detail: `${missingComments.length} item${missingComments.length > 1 ? 's' : ''} marked as "No" require${missingComments.length === 1 ? 's' : ''} a comment before saving.`
      });
      return;
    }

    state.saving = true;
    const items: UpsertProjectChecklistItemDto[] = state.rows
      .filter(r => r.isActive)
      .map(r => ({
        checklistItemId: r.itemId,
        answer: r.answer,
        comment: r.comment || undefined
      }));

    this.checklistService.upsert(this.projectId, state.template.id, items).pipe(
      catchError(() => {
        this.messageService.add({ severity: 'error', summary: 'Save failed', detail: `Could not save "${state.template.name}".` });
        state.saving = false;
        return of(null);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(saved => {
      if (saved !== null) {
        state.isDirty = false;
        state.saving = false;
        this.emitDirty(this.templateStates.some(s => s.isDirty));
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: `"${state.template.name}" saved.` });
      }
    });
  }

  save(): void {
    this.templateStates.filter(s => s.isDirty).forEach(s => this.saveTemplate(s));
  }

  private emitDirty(value: boolean): void {
    this.dirtyChange.emit(value);
  }
}
